import { isNativeApp } from './platform'
import { getInstance } from './instance'

/**
 * [Native B1, LEGAL] The one place a rejected `gs_` outlives its 401.
 *
 * On a server-driven revoke/expiry the 401 interceptor clears the grant credential — but the
 * signed-out landing must still confirm the PII wipe (`POST /grant/wipe-ack`, which stamps
 * WipeConfirmedAt by the raw session token and works for a dead session). On web the session
 * cookie survives the revoke; on native the interceptor just deleted the only copy. So the
 * rejected value is kept HERE, in process memory only — never persisted, never logged — bound to
 * the instance it came from, and handed out for exactly one URL: the wipe-ack. It is dropped as
 * soon as that ack is sent, when a new grant credential is stored, and on an organization switch.
 */

const WIPE_ACK_PATH = '/grant/wipe-ack'

let retained: { host: string; token: string } | null = null

function currentHost(): string | null {
  const inst = getInstance()
  return inst ? new URL(inst.origin).host : null
}

export function isWipeAckUrl(url: string | undefined): boolean {
  const u = url ?? ''
  return u === WIPE_ACK_PATH || u.startsWith(WIPE_ACK_PATH + '?')
}

/** Called only by the 401 interceptor, right after it cleared a rejected grant credential. */
export function retainRejectedGrant(token: string): void {
  if (!isNativeApp()) return
  const host = currentHost()
  retained = host ? { host, token } : null
}

/** The retained rejected `gs_` for the CURRENT instance, or null. Only the wipe-ack may use it. */
export function rejectedGrantForWipeAck(): string | null {
  if (!isNativeApp() || !retained) return null
  return retained.host === currentHost() ? retained.token : null
}

export function dropRejectedGrant(): void {
  retained = null
}
